
import AppBrowse from "@/components/browse/Browse.vue";
import AppImportCsvModal from "@/components/products/ImportCsvModal.vue";
import {Product} from "@/models/base";
import {BrowseService} from "@/services/browse-service";
import {ProductService} from "@/services/product-service";
import {ToolbarService} from "@/services/toolbar-service";
import {IBrowseDataSource} from "@/utils/browse/browse-data-source";
import Vue from "vue";
import {UserConfigService} from "../../services/user-config-service";
import AppProductGroupEditCard from "@/components/product-group/ProductGroupEditCard.vue";
import AppProductEditSellPricesModal from "@/components/products/ProductEditSellPricesModal.vue";
import AppStockEditSingle from "@/components/stock/StockEditSingle.vue";
import {IQueryParams} from "@/utils/query-params";
import {AuthService} from "@/services/auth-service";
import {MODULE} from "@/config/modules";
import {JobService} from "@/services/job-service";
import {StockService} from "@/services/stock-service";
import {ISelectItem} from "winfakt-vue-components";
import {HttpError} from "@/utils/services/http-error";
import {BrowseState} from "@/utils/browse/browse-state";
import {FetchedTotal} from "@/utils/views/fetched-total";
import {View} from "@/models/view";
import {Tier} from "@/utils/payments/tiers";
import {ExtraFieldNamesConfig} from "@/models/dossier-configs/extra-field-names-config";
import {DossierConfigService} from "@/services";
import { ProductGroup } from "@/models/base/product-group";
import AppCreateAutomaticLabelsModal from "@/components/products/CreateAutomaticLabelsModal.vue";
import AppSetGroupModal from "@/components/products/SetGroupModal.vue";
import AppSetSizeTableModal from "@/components/products/SetSizeTableModal.vue";
import AppProductQuickEditSellPrice from "@/components/products/ProductQuickEditSellPrice.vue";
import AppProductQuickEditSellPriceIncVat from "@/components/products/ProductQuickEditSellPriceIncVat.vue";
import AppProductQuickEditMainSupplierBuyCatalogPrice from "@/components/products/ProductQuickEditMainSupplierBuyCatalogPrice.vue";
import AppProductQuickEditMainSupplierBuyNettoPrice from "@/components/products/ProductQuickEditMainSupplierBuyNettoPrice.vue";
import AppProductQuickEditGroup from "@/components/products/ProductQuickEditGroup.vue";
import AppPriceListBrowseWrapper from "@/components/price-lists/PriceListBrowseWrapper.vue";
import AppExcelMapper from "@/components/products/ExcelMapperWrapper.vue";
import { ScannerListener } from "@/utils/scanner";

interface IQuickEditSellPriceData {
	category:number;
	product:Product;
}

export default Vue.extend({
	data() {
		return {
			includeVat: false,
			priceCategory: 0,
			endSentence: "",
			uploadCSVModal: false,
			addStockModal: false,
			editExtraFieldsModal: false,
			editStockModal: false,
			editGroupsModal: false,
			editPriceSettingsModal: false,
			selectedItems: ProductService.selectedProducts,
			JobService,
			browseState: null as null | BrowseState,
			createAutomaticLabelsModalOpen: false,
			setGroupModalOpen: false,
			setSizeTableModalOpen: false,
			quickEditSellPriceModal: null as null | IQuickEditSellPriceData,
			quickEditSellPriceIncVatModal: null as null | IQuickEditSellPriceData,
			toQuickEditMainSupplierCatalogPriceProduct: null as null | Product,
			toQuickEditMainSupplierNettoPriceProduct: null as null | Product,
			toQuickEditGroupProduct: null as null | Product,
			showPriceListsModal: false,
			excelMapperOpen: false,
			scanToSelect: false
		};
	},
	props: {
		browseId: Number,
		controlsDisabled: Boolean,
		searchEnabled: Boolean,
	},
	computed: {
		dataSource(): IBrowseDataSource<Product> {
			let elem = this;
			return {
				getFullElement: (el:Product)=>ProductService.getProduct(el.ID),
				getSettings: () => BrowseService.getBrowse(this.browseId),
				getElements: (params: IQueryParams) => ProductService.getProducts(params),
				getTotals(view: View): Promise<FetchedTotal[]> {
					return ProductService.getTotals(view);
				},
				edit(product: Product) {
					elem.$router.push(`/products/edit/${product.ID}`);
				},
				async delete(products: Product[]) {
					await elem.deleteFunction.bind(elem)(products);
					return true;
				},
				addRoute: `/products/new`,
				browseName: "Products",
				modelName: "Product",
				info(product: Product) {
					elem.$router.push(`/products/info/${product.ID}`);
				},
				browsePermissions: () => UserConfigService.getPermissionSettings().ProductPermissions,
				getOnElementUpdateSocketEvent: (item: Product) => `/product/${item.ID}/update`,
				onSocketElementUpdated: (data: any) => new Product(data),
				extraFieldSettings: {
					getExtraFields: ProductService.getExtraFields.bind(ProductService),
					nameSettings: DossierConfigService.getExtraFieldNamesConfigs().ProductNames,
					batchEdit: ProductService.batchEditExtraField.bind(ProductService),
					edit: (ProductService.updateExtraFields as any).bind(ProductService)
				}
			};
		},
		canWrite(): boolean {
			return UserConfigService.hasCurrentUserPermission((p) => p.ProductPermissions.hasWritePermissions());
		},
		hasStockModule(): boolean {
			return AuthService.hasModule(MODULE.VOORRAADBEHEER);
		},
		isComputingStock(): boolean {
			return this.JobService.getJobs().findIndex(a => a.Name == "compute-stock") != -1;
		},
		extraFieldNameSettings(): ExtraFieldNamesConfig {
			return DossierConfigService.getExtraFieldNamesConfigs().ProductNames;
		},
		// eslint-disable-next-line max-lines-per-function
		buttonBarItems(): ISelectItem<string | Function>[] {
			let result: ISelectItem<string | Function>[] = [
				{
					text: this.$ct("tooltip.import-csv"),
					value: "openUploadCsvModal",
					icon: "upload",
					disabled: !this.canWrite || !AuthService.hasModule(MODULE.IMPORT),
				},
				{
					text: this.$ct("excel-conversion"),
					value: ()=>this.excelMapperOpen = true,
					icon: "file-excel",
					disabled: !this.canWrite || !AuthService.hasModule(MODULE.IMPORT),
				},
				{
					text: this.$ct("product-group-edit"),
					value: "openEditGroupsModal",
					icon: "object-group",
					disabled: !ProductGroup.canEditGroups()
				},
				{
					text: this.scanToSelect ? this.$ct("disable-scan-to-select") : this.$ct("enable-scan-to-select"),
					value: ()=>this.scanToSelect = !this.scanToSelect,
					icon: "barcode"
				}
			];
			if (this.hasStockModule) {
				result.push({
					text: this.$ct("recompute-stock"),
					value: "recomputeStockClicked",
					icon: "cubes",
					disabled: !this.canWrite
				});
			}
			if (AuthService.wfCompany.Tier >= Tier.ENTERPRISE) {
				result.push({
					text: this.$ct("product-price-edit"),
					value: "openEditPriceModal",
					icon: "tag",
					disabled: this.selectedItems.length == 0
				},{
					text: this.$ct("mark-for-label-printer"),
					value: "markForLabelPrinter",
					icon: "tag",
					disabled: this.selectedItems.length == 0
				},{
					text: this.$ct("unmark-for-label-printer"),
					value: "unmarkForLabelPrinter",
					icon: "slash",
					disabled: this.selectedItems.length == 0
				},{
					text: this.$ct("set-group-for-selected"),
					value: ()=>this.setGroupModalOpen=true,
					icon: "object-group",
					disabled: this.selectedItems.length == 0
				});
			}

			if (AuthService.hasModule(MODULE.MATENTABEL)) {
				result.push({
					text: this.$ct("set-size-table-for-selected"),
					value: ()=>this.setSizeTableModalOpen = true,
					icon: "ruler",
					disabled: !this.canWrite || this.selectedItems.length == 0
				});
			}

			if (AuthService.hasModule(MODULE.ETIKETTEN)) {
				result.push({
					text: this.$ct("print-automatic-labels"),
					value: "openPrintAutomaticLabelsModal",
					icon: "tags",
					disabled: this.selectedItems.length == 0
				});
			}

			if (this.selectedItems.length == 1){
				result.push({
					text: this.$ct("stock-movement"),
					icon: "pallet",
					value: "openStockMovement"
				});
			}

			if (AuthService.isDevModeEnabled){
				result.push({
						text: "Stock herberekenen van geselecteerde artikellen",
						icon: "dolly-flatbed",
						value: this.computeStock,
						disabled: this.selectedItems.length == 0
					},
					{
						text: "Artikelen opnieuw opslaan",
						value: this.resaveProducts,
						icon: "save"
					}
				);
			}

			if (AuthService.hasModule(MODULE.EXTERNE_PRODUCT_CATALOGUS)) {
				result.push({
					text: this.$ct("price-lists"),
					icon: "tags",
					value: this.openPriceListsModal
				});
			}

			return result;
		},
		selectedProductId(): number {
			if (!this.selectedItems[0]) {
				return 0;
			}
			return this.selectedItems[0].ID;
		},
		scanToSelectShouldBeOn():boolean {
			if (this.$route.path != "/products") return false;
			if (!this.scanToSelect) return false;
			return true;
		}
	},
	watch: {
		"$route.path": {
			immediate: true,
			handler() {
				if (this.$route.path != "/products") return;
				if (!this.controlsDisabled) {
					ToolbarService.title = this.$ct("products");
				}
			}
		},
		"scanToSelectShouldBeOn": {
			immediate: true,
			handler(){
				if (this.scanToSelectShouldBeOn){
					ScannerListener.onInput(this.onBarcodeScanner);
				}else{
					ScannerListener.offInput(this.onBarcodeScanner);
				}
			}
		}
	},
	methods: {
		async computeStock(item?:Product){
			let items = this.selectedItems;
			if (item) {
				items = [item];
			}
			await StockService.computeStock(undefined, items.map(p=>p.ID));
		},
		openPrintAutomaticLabelsModal(){
			this.createAutomaticLabelsModalOpen = true;
		},
		async recomputeStockClicked() {
			let msg = this.$ct("are-you-sure-you-want-to-recompute-stock-this-can-take-some-time");
			let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(msg).show();
			if (!confirm) return;
			await StockService.computeStock();
		},
		openUploadCsvModal() {
			this.uploadCSVModal = true;
		},
		openEditGroupsModal() {
			this.editGroupsModal = true;
		},
		openEditPriceModal() {
			this.editPriceSettingsModal = true;
		},
		showProductCannotBeDeletedDialog(product: Product) {
			let msg = this.$ct("product-with-sku-cannot-be-deleted-because-sales-or-stocks", {sku: product.Sku}).toString();
			this.$wf.dialog(msg, this.$ct("common.Ok"), this.$ct("deleting-failed"));
		},
		updateView() {
			if (!this.browseState) return;
			this.browseState.fetch();
		},
		async deleteFunction(products: Product[]) {
			try {
				let result = await ProductService.delProducts(products);
				if (result.UnableToDeleteProducts.length > 0) {
					let msg = this.$ctc("products-cannot-be-deleted-because-sales-or-stocks-set-to-inactive", result.UnableToDeleteProducts.length).toString();
					let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(msg).show();
					if (!confirm) return;
					await ProductService.setProductsActive(result.UnableToDeleteProducts, false);
				}
			} catch (err) {
				let e = err as HttpError;
				if (e.key == "CANNOT_DELETE_PRODUCT") {
					let id = e.data[0];
					for (let p of products) {
						if (`${p.ID}` == id) {
							e.dontShow = true;
							this.showProductCannotBeDeletedDialog(p);
							return;
						}
					}
				}
				throw err;
			}
		},
		async markForLabelPrinter() {
			let changedProducts = await ProductService.markForLabelPrinter(this.selectedItems);
			this.$wf.notify(this.$ctc("products-marked-for-label-printer", changedProducts), "success", 3000, "check");
		},
		async unmarkForLabelPrinter() {
			let changedProducts = await ProductService.unmarkForLabelPrinter(this.selectedItems);
			this.$wf.notify(this.$ctc("products-unmarked-for-label-printer", changedProducts), "success", 3000, "check");
		},
		async editPriceSettings() {
			this.editPriceSettingsModal = !this.editPriceSettingsModal;
		},
		onProductSellPricesEditSaved(){
			this.selectedItems.splice(0, this.selectedItems.length);
			if (!this.browseState) return;
			this.browseState.fetch();
		},
		onStockSaved(){
			this.editStockModal = false;
			this.selectedItems.splice(0, this.selectedItems.length);
			this.updateView();
		},
		openStockMovement(){
			if (this.selectedItems.length != 1) return;
			window.open(`/stock-movement/${this.selectedItems[0].ID}`, "_blank");
			//this.$router.push(`/stock-movement/${this.selectedItems[0].ID}`);
		},
		async resaveProducts(){
			await ProductService.resaveProducts(this.selectedItems.map(c=>c.ID));
			this.$wf.notify("Artikelen opnieuw opslaan is gestart", "info", 3000, "info-circle");
		},
		onColClick(result:string, value:Product){
			if (result.startsWith("edit-sell-price/")){
				let category = parseInt(result.replaceAll("edit-sell-price/", ""));
				if (!category || Number.isNaN(category)) return;
				this.quickEditSellPrice(value, category);
			}else if (result.startsWith("edit-sell-price-inc-vat/")){
				let category = parseInt(result.replaceAll("edit-sell-price-inc-vat/", ""));
				if (!category || Number.isNaN(category)) return;
				this.quickEditSellPriceIncVat(value, category);
			}else if (result == "edit-buy-netto-price"){
				this.toQuickEditMainSupplierNettoPriceProduct = value;
			}else if (result == "edit-buy-catalog-price"){
				this.toQuickEditMainSupplierCatalogPriceProduct = value;
			}else if (result == "edit-product-group") {
				this.toQuickEditGroupProduct = value;
			}
		},
		quickEditSellPrice(product:Product, category:number){
			this.quickEditSellPriceModal = {
				category,
				product
			};
		},
		quickEditSellPriceIncVat(product:Product, category:number){
			this.quickEditSellPriceIncVatModal = {
				category,
				product
			};
		},
		async refreshProduct(product:Product){
			if (!this.browseState) return;
			this.browseState.refreshSpecificRecords([product.ID]);
		},
		openPriceListsModal(){
			this.showPriceListsModal = true;
		},
		async onBarcodeScanner(input:string){
			if (!this.scanToSelect) return;
			if (this.$route.path != "/products") return;
			if (this.selectedItems.find(p=>p.BarCode == input)) return;
			let product = await ProductService.getByBarCode(input);
			if (this.selectedItems.find(p=>p.BarCode == input)) return;
			this.selectedItems.push(product);
		}
	},
	mounted() {
		if (!this.controlsDisabled) {
			ToolbarService.title = this.$ct("products");
		}
	},
	destroyed() {
		ScannerListener.offInput(this.onBarcodeScanner);
		ProductService.selectedProducts = [];
	},
	components: {
		AppBrowse,
		AppImportCsvModal,
		AppProductGroupEditCard,
		AppProductEditSellPricesModal,
		AppStockEditSingle,
		AppCreateAutomaticLabelsModal,
		AppSetGroupModal,
		AppSetSizeTableModal,
		AppProductQuickEditSellPrice,
		AppProductQuickEditSellPriceIncVat,
		AppProductQuickEditMainSupplierBuyCatalogPrice,
		AppProductQuickEditMainSupplierBuyNettoPrice,
		AppProductQuickEditGroup,
		AppPriceListBrowseWrapper,
		AppExcelMapper
	},
	i18nEx: {
		componentPrefix: "components.products"
	}
});
